/**
 * Blindaje de rutas para el equipo (un profesional de un venue, `session.is_staff`).
 *
 * El menú lateral ya les muestra solo lo suyo, pero las RUTAS quedaban abiertas: escribiendo
 * la URL —o siguiendo un banner— un profesional entraba a `/ajustes/medios-de-cobro` o a
 * `/facturacion`, pantallas del dueño que no puede usar (reporte de JP, 28/jul/2026).
 *
 * Acá se invierte el default: para un staff, lo que no está en la lista NO existe y cae en su
 * Agenda — nunca en un 404 ni en una pantalla en blanco. Sumar una pantalla para el equipo es
 * agregar una línea; olvidarse de restar una ya no expone nada.
 *
 * Ojo: esto es producto, no seguridad. Quien tiene que rechazar de verdad es la API — el token
 * del staff sigue sirviendo para pegarle a mano a cualquier endpoint.
 */

/** Rutas exactas: habilitan la pantalla pero NO lo que cuelga de ella (editar es del dueño). */
const STAFF_ALLOWED_EXACT = [
  '/actividad',
  '/servicios', // solo lectura y con `services_view` (ver abajo); /servicios/edicion-global/* es del dueño
  '/notificaciones',
  '/contenido-ig', // el hub: al equipo le ofrece sólo el armador de posteos
  '/contenido-ig/posteo', // armador de posteos; el flag exige agenda_view + agenda_edit (legado, derivado)
  '/historias', // el QR de «Tu mes»: redirige a /analytics/historias con su ?pieza=
  '/compartir-horarios', // ruta vieja del armador: redirige a /contenido-ig/posteo
  '/ajustes', // sus propios datos personales
  '/ajustes/info',
  '/fichaje/marcar' // escanear el código de la pantalla de fichaje para marcar su llegada
];

/** Prefijos: la pantalla y todo lo que cuelga de ella son del equipo. */
const STAFF_ALLOWED_PREFIXES = [
  '/mis-cursos', // un profesional del equipo también compra cursos
  // Analítica y sus secciones (v2: /analytics/resumen…). El alcance lo decide la API
  // (`analytics_view`) y una sección que no es del equipo vuelve a Resumen.
  '/analytics',
  '/agenda', // + /ingresar-reserva y /bloquear-horarios (con su permiso, ver abajo), /horarios-de-trabajo
  '/clientes', // filtrado además por permisos (useClientPermissions)
  '/servicio/evento/', // detalle de un evento — EventDetails tiene ramas de staff
  // Sistema y auth: no son pantallas de negocio, y cortarlas rompe logins y callbacks.
  '/login',
  '/logout',
  '/registro',
  '/callback',
  '/new/user',
  '/plans/redirect',
  '/bienvenido',
  '/google-calendar/callback',
  '/google/drive/callback',
  '/mercado-pago/callback'
];

/**
 * Finanzas en SOLO LECTURA, detrás del permiso `finanzas_view` (JP, 1/sep/2026).
 *
 * Van una por una y no como prefijo `/finanzas` a propósito: así `/finanzas/caja` —que es
 * una pantalla de OPERACIÓN, con apertura y cierre de caja— queda afuera sin depender de
 * que nadie se acuerde de restarla. Sumar una sección de lectura es agregar una línea.
 */
const STAFF_FINANZAS_PATHS = [
  '/finanzas',
  '/finanzas/ventas',
  '/finanzas/por-cobrar',
  '/finanzas/cobros',
  '/finanzas/gastos',
  '/finanzas/resultado',
  '/finanzas/comisiones'
];

/**
 * Pantallas con permiso propio (permisos v2, 14/10): sin él, la URL cae en la Agenda en vez de
 * abrir un formulario que la API rechaza al guardar, o un catálogo que el menú no le ofrece.
 * Horarios de trabajo no va acá: su pantalla misma vuelve a la Agenda con el motivo
 * (`ActionWorkingHours`).
 */
type StaffPathPermission = 'bookings_create' | 'blocked_hours_edit' | 'services_view';

const STAFF_PERMISSION_PATHS: Record<string, StaffPathPermission> = {
  '/agenda/ingresar-reserva': 'bookings_create',
  '/agenda/bloquear-horarios': 'blocked_hours_edit',
  '/servicios': 'services_view'
};

export const isStaffAllowedPath = (
  pathname: string,
  permissions?: ({ finanzas_view?: boolean } & Partial<Record<StaffPathPermission, boolean>>) | null
): boolean => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const requiredPermission = STAFF_PERMISSION_PATHS[path];
  if (requiredPermission && permissions?.[requiredPermission] !== true) return false;
  if (STAFF_ALLOWED_EXACT.includes(path)) return true;
  if (permissions?.finanzas_view && STAFF_FINANZAS_PATHS.includes(path)) return true;
  return STAFF_ALLOWED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
};
